import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

// Shift hand-off notes — what the last shift needs the next one to know.
// CJ 2026-08-21.
//
// "very important for closers to be able to do this for the openers ... extra
// prep done or missed, any events for the next day or special guest notes, lets
// say for example that someone is going to pick up a dozen sandwiches at open."
//
// Two halves, and they are deliberately different in tone:
//
//   READING  is a to-do. Unacknowledged notes sit at the top of My To-Do and
//            stay there until this person — not "somebody" — has ticked them.
//            Per-person, because "who actually read the note about the dozen
//            sandwiches" is exactly the question asked when the sandwiches are
//            not ready.
//
//   WRITING  is optional and folded away. A composer permanently open in
//            everyone's to-do list is a box people learn to scroll past, and
//            most shifts have nothing to hand over.
//
// Not to be confused with the shift handoff REPORT, which is the backward
// looking "here is what the last shift left undone" and goes to a manager.
export function ShiftHandoffNotes({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [pending, setPending] = useState([]);
  const [categories, setCategories] = useState([]);
  const [composing, setComposing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ category: 'prep', body: '', when: 'tomorrow' });

  const load = useCallback(async () => {
    const r = await api('GET', '/api/handoff-notes/pending', null, headers).catch(() => null);
    if (r) { setPending(r.notes || []); setCategories(r.categories || []); }
  }, [currentUser.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function ack(id) {
    setBusy(true);
    try {
      await api('POST', `/api/handoff-notes/${id}/ack`, {}, headers);
      // Drop it locally rather than refetching — the row disappearing the
      // instant it is ticked is the whole feedback that it worked.
      setPending((rows) => rows.filter((n) => n.id !== id));
    } catch (e) { setMsg(e?.message || 'Could not acknowledge that'); }
    finally { setBusy(false); }
  }

  async function post() {
    const body = form.body.trim();
    if (!body) { setMsg('Write the note first'); return; }
    setBusy(true); setMsg('');
    try {
      // for_date omitted means tomorrow, which is the closer-to-opener case
      // this exists for. "Later today" is the exception and says so.
      const payload = { category: form.category, body };
      if (form.when === 'today') payload.for_date = new Date()
        .toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      await api('POST', '/api/handoff-notes', payload, headers);
      setForm({ category: 'prep', body: '', when: 'tomorrow' });
      setComposing(false);
      setMsg('Left for the next shift.');
      setTimeout(() => setMsg(''), 4000);
    } catch (e) { setMsg(e?.message || 'Could not leave that note'); }
    finally { setBusy(false); }
  }

  const labelFor = (id) => (categories.find((c) => c.id === id) || {}).label || id;
  const nothingToShow = pending.length === 0 && !composing;

  return (
    <div className="card" style={{ borderLeft: '4px solid #4a6b7c' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div className="text-base font-semibold">Shift hand-off</div>
        {pending.length > 0 && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#8a6d1f' }}>
            {pending.length} to read
          </span>
        )}
        <button onClick={() => setComposing((v) => !v)} className="btn-secondary"
                style={{ marginLeft: 'auto', fontSize: 11.5, whiteSpace: 'nowrap' }}>
          {composing ? 'Cancel' : 'Leave a note'}
        </button>
      </div>

      {nothingToShow && (
        <div style={{ fontSize: 12.5, color: '#757575', marginTop: 6 }}>
          Nothing from the last shift. Leaving a note takes ten seconds and saves the opener ten minutes.
        </div>
      )}

      {pending.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 9 }}>
          {pending.map((n) => (
            <div key={n.id} style={{ border: '1px solid #eee', borderRadius: 8,
                                     padding: '9px 11px', borderLeft: '3px solid #4a6b7c' }}>
              <div style={{ display: 'flex', gap: 7, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 9.5,
                               fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                               background: '#e9f0f4', color: '#4a6b7c', padding: '1px 5px',
                               borderRadius: 3 }}>
                  {labelFor(n.category)}
                </span>
                <span style={{ fontSize: 11, color: '#757575' }}>
                  {n.author_name} · for {String(n.for_date).slice(5)}
                </span>
              </div>
              <div style={{ fontSize: 13.5, color: '#212121', marginTop: 5, whiteSpace: 'pre-wrap' }}>
                {n.body}
              </div>
              <button onClick={() => ack(n.id)} disabled={busy} className="btn-primary"
                      style={{ marginTop: 8, fontSize: 11.5 }}>
                Got it
              </button>
            </div>
          ))}
        </div>
      )}

      {composing && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    style={{ fontSize: 12.5, padding: '6px 8px', borderRadius: 6,
                             border: '1px solid #ddd', fontFamily: 'inherit' }}>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <select value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })}
                    style={{ fontSize: 12.5, padding: '6px 8px', borderRadius: 6,
                             border: '1px solid #ddd', fontFamily: 'inherit' }}>
              <option value="tomorrow">For the next shift (tomorrow)</option>
              <option value="today">For later today</option>
            </select>
          </div>
          <textarea value={form.body} rows={3} maxLength={2000}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    placeholder="e.g. Someone is picking up a dozen sandwiches at open — they're prepped and labelled in the walk-in."
                    style={{ fontSize: 13, padding: 9, borderRadius: 6, border: '1px solid #ddd',
                             fontFamily: 'inherit', resize: 'vertical' }} />
          <button onClick={post} disabled={busy} className="btn-primary" style={{ alignSelf: 'flex-start' }}>
            {busy ? 'Leaving…' : 'Leave for the next shift'}
          </button>
        </div>
      )}

      {msg && <div style={{ fontSize: 12, color: '#4a6b7c', marginTop: 7 }}>{msg}</div>}
    </div>
  );
}
